<script>
import {Graylog} from '../graylog'
import Expandable from './Expandable.vue'
import GroupedDurations from './GroupedDurations.vue'

export default {
  name: 'PerformanceDashboard',

  components: {
    Expandable,
    GroupedDurations
  },

  props: {
    deployments: Array,
    searchParams: Object,

    basicQuery: String,
    pieCharts: Array,
    durationField: String,
    title: String,
    cacheKey: String,
    chartTitle: { default: 'Durations' },

    facilityPrefix: {
      type: String,
      default: ''
    },

    groupingAggregation: {
      default: 'total'
    },

    showTotalCounts: {
      default: true
    },

    durationFilters: {
      type: Array,
      default() {
        return [0.0, 0.2, 0.5, 1.0, 1.5, 2.0, 3.0, 5.0, 10.0, 20.0, 40.0, 60.0]
      }
    },

    showGraylogLink: {
      default: true
    },

    maxDurationFilters: {
      type: Array,
      default() {
        return [1.0, 1.5, 2.0, 3.0, 5.0, 10.0, 20.0, 40.0, 60.0, 1000]
      }
    }
  },

  watch: {
    searchParams: {
      deep: true,
      handler() { this.fetch() }
    },
    searchQuery() { this.fetch() },
    visible() { this.fetch(); },
    pieChartsVisible() { this.fetchPieCharts(); }
  },

  data() {

    let cachedJSON = localStorage.getItem(this.cacheKey);
    let cachedData = {};
    if (cachedJSON) {
      cachedData = JSON.parse(cachedJSON);
    }

    return {
      lineCharts: [],
      pieChartData: {},

      additionalFilter: cachedData.additionalFilterInputValue || '',
      additionalFilterInputValue: cachedData.additionalFilterInputValue || '',

      minimumDuration: cachedData.minimumDuration || 1,
      maximumDuration: cachedData.maximumDuration || 1000,

      visible: cachedData.visible,
      pieChartsVisible: true,

      showGroupedDurations: "",

      totalCount: 0
    }
  },

  mounted() {
    window.Graylog = Graylog
    this.fetch();
  },

  computed: {
    calculatedPerDay() {
      let days = this.searchParams.range / (24*60*60);
      return Math.round(this.totalCount / days);
    },

    searchQuery() {
      let query = `facility:"${this.facilityPrefix}${this.searchParams.facility}" AND ${this.basicQuery} AND ${this.durationField}:[${this.minimumDuration} TO ${this.maximumDuration}]`;
      if (this.additionalFilter.length > 0) {
        query += ` AND (${this.additionalFilter})`;
      }
      return query;
    },

    parsedTitle() {
      return this.title
        .replace('{{minimumDuration}}', this.minimumDuration)
        .replace('{{maximumDuration}}', this.maximumDuration);
    },

    graylogUrl() {
      let params = {
        rangetype: 'relative',
        relative: this.searchParams.range,
        q: this.searchQuery
      };

      let queryString = Object.keys(params).map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`).join('&');

      return `${Graylog.url}/search?${queryString}`;
    }

  },

  methods: {

    fillCache() {
      localStorage.setItem(this.cacheKey, JSON.stringify({
        minimumDuration: this.minimumDuration,
        maximumDuration: this.maximumDuration,
        additionalFilterInputValue: this.additionalFilterInputValue,
        visible: this.visible
      }));
    },

    applyPieClickEvents(key) {
      this.$nextTick(() => {
        if (!this.$refs[`pie-${key}`] || this.$refs[`pie-${key}`].length == 0) { return; }

        this.$refs[`pie-${key}`][0].chart.chart.canvas.onclick = (ev) => {
          var element = this.$refs[`pie-${key}`][0].chart.chart.getElementAtEvent(ev);
          this.setFieldFilter(key, element[0]._model.label);
        };
      });
    },

    setFieldFilter(key, value) {
      let filterValue = `${key}:"${value}"`;

      let filterParts = this.additionalFilterInputValue.split(' AND ').filter( (s) => {
        return s.trim().length > 0;
      });
      let existingIndex = filterParts.indexOf(filterValue);
      if (existingIndex >= 0) {
        filterParts.splice(existingIndex, 1);
      } else {
        filterParts.push(filterValue);
      }

      this.additionalFilterInputValue = filterParts.join(' AND ');
      this.setAdditionalFilter();
    },

    setAdditionalFilter() {
      this.additionalFilter = this.additionalFilterInputValue;
    },

    fetchPieCharts() {
      if (!this.visible && !this.pieChartsVisible) { return; }

      for(let pieChart of this.pieCharts) {
        this.fetchPieChart(pieChart.field, pieChart.label);
      }
    },

    fetchPieChart(field, label) {
      Graylog.getTerms(this.searchQuery, {
        range: this.searchParams.range,
        field: field
      }).then(response => {
        this.$set(this.pieChartData, field, {
          field: field,
          title: label,
          data: response.data.terms
        });
        this.applyPieClickEvents(field)
      });
    },

    fetch() {
      this.fillCache();

      if (!this.visible) { return; }

      // get response times
      Graylog.getFieldHistogramm(this.durationField, this.searchQuery, {
        range: this.searchParams.range,
        interval: this.searchParams.interval
      }).then(response => {
        //console.log(this.deployments[0].finished_at)
        this.lineCharts = [
          {
            data: [
              { name: 'avg', data: Graylog.toTimeline(response.data.results, 'mean') },
              { name: 'min', data: Graylog.toTimeline(response.data.results, 'min') },
              { name: 'max', data: Graylog.toTimeline(response.data.results, 'max') },
            ],
            title: this.chartTitle,
            height: '450px',
            //lineAtIndex: [this.deployments[0].finished_at]
          }
        ]

        if (this.showTotalCounts) {
          this.lineCharts[0].data.push(
            { name: 'total', data: Graylog.toTimeline(response.data.results, 'total') }
          )

          this.lineCharts.push({
            data: [
              { name: 'total_count', data: Graylog.toTimeline(response.data.results, 'total_count') },
            ],
            title: '#',
            height: '350px'
          })
        }

        this.totalCount = Object.values(response.data.results).reduce( (previousValue, item) => {
            return previousValue + item.total_count;
          }, 0
        );

      })

      this.fetchPieCharts()
    }

  }
}
</script>

<template>
  <Expandable :title="parsedTitle" v-model="visible" :colorIndex="0">

    <div class="grid-x grid-margin-x">

      <div class="cell medium-2">
        <select v-model="minimumDuration">
          <option v-for="duration in durationFilters" :key="duration" :value="duration">{{duration}} sec</option>
        </select>
      </div>
      <div class="cell medium-2">
        <select v-model="maximumDuration">
          <option v-for="duration in maxDurationFilters" :key="duration" :value="duration">{{duration}} sec</option>
        </select>
      </div>
      <div class="cell medium-3">
        <select v-model="showGroupedDurations">
          <option value="">Do not group</option>

          <option
            v-for="pieChart in pieChartData" v-bind:key="`option-${pieChart.field}`"
            :value="pieChart.field"
          >
            Group by {{ pieChart.title }}
          </option>
        </select>
      </div>

      <div class="cell medium-5">
        <div class="grid-x">
          <div class="cell small-10 large-11">
            <input ref="additionalFilter"
              v-model="additionalFilterInputValue"
              @keyup.enter="setAdditionalFilter"
              type="text"
              placeholder="Additional Filter (Graylog Syntax)"
              />
          </div>
          <div class="cell small-2 large-1">
            <a @click="setAdditionalFilter" class="button block">GO</a>
          </div>
        </div>
      </div>

    </div>

    <br>

    <div class="grid-x grid-margin-x">

      <div :class="`cell medium-${showGraylogLink ? '9' : '12'}`">
        <div v-if="showGroupedDurations">
          <GroupedDurations
            v-if="pieChartData[showGroupedDurations]"
            :chartTitle="chartTitle"
            :aggregation="groupingAggregation"
            :filterKey="showGroupedDurations"
            :filterValues="Object.keys(pieChartData[showGroupedDurations].data)"
            :searchQuery="searchQuery"
            :searchParams="searchParams"
            :durationField="durationField"
          >
          </GroupedDurations>
        </div>
        <div v-else>
          <div v-for="chartData in lineCharts" v-bind:key="chartData.title">
            <line-chart :data="chartData.data" :ytitle="chartData.title" :height="chartData.height" :curve="false"></line-chart>
            <br/><br/>
          </div>
        </div>

        <Expandable title="Pies" v-model="pieChartsVisible" :colorIndex="2">
          <div class="grid-x grid-margin-x">
            <div v-for="pieChart in pieChartData" v-bind:key="pieChart.field" class="cell medium-3">
              <h4>{{pieChart.title}}</h4>
              <pie-chart :ref="'pie-'+pieChart.field" :height="250" :data="pieChart.data" :legend="false" :donut="true"></pie-chart>
            </div>
          </div>
        </Expandable>
      </div>

      <div v-if="showGraylogLink" class="cell medium-3">
        <a :href="graylogUrl" class="button secondary block" target="_blank">Open in Graylog &raquo;</a>
        <div class="total-sum">
          <div>
            <span class="small">SUM</span>
            <span class="large">{{ totalCount }}</span>
          </div>
          <span class="smaller">{{ parsedTitle }} In The Past {{ searchParams.rangeText }}</span><br>
          <span class="smaller">Roughly <span class="medium">{{ calculatedPerDay }}</span> Per Day</span>
        </div>
      </div>

    </div>
    <br/><br/>

  </Expandable>

</template>

<style scoped>
.block {
  display: block;
}
.small {
  font-size: 1rem;
}
.smaller {
  font-size: 0.7rem;
  line-height: 5px !important;
}
.medium {
  font-size: 1.3rem;
}
.large {
  font-size: 5rem;
}
</style>
